import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "./AuthContext";
import {
  getUserCart,
  addToCart as addToCartService,
  updateCartItemQuantity as updateCartItemQuantityService,
  removeFromCart as removeFromCartService,
  clearCart as clearCartService,
} from "../services/cartService";
import { UserCart, CartItemWithProduct } from "../types/cart";
import { readDocument, readCollection } from "../firebase/db";
import { COLLECTIONS } from "../firebase/collectionNames";
import { Product } from "../types/product";
import { getSafeImageUrl } from "../utils/imageUtils";

interface CartContextValue {
  cart: UserCart | null;
  cartItemsWithProducts: CartItemWithProduct[];
  loading: boolean;
  error: string | null;
  addToCart: (mangoId: string, quantity?: number) => Promise<void>;
  updateQuantity: (mangoId: string, quantity: number) => Promise<void>;
  removeItem: (mangoId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  getCartItemCount: () => number;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export const useCart = (): CartContextValue => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within CartProvider");
  }
  return ctx;
};

export const CartProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { userId, isAuthenticated } = useAuth();
  const [cart, setCart] = useState<UserCart | null>(null);
  const [cartItemsWithProducts, setCartItemsWithProducts] = useState<CartItemWithProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch product details for a mangoId
   * mangoId can be either the Firestore document ID or a generated ID from product name
   */
  const fetchProductDetails = useCallback(async (mangoId: string): Promise<Product | null> => {
    try {
      // First try to fetch by document ID
      let product = await readDocument<Product>(COLLECTIONS.products, mangoId);
      
      // If not found, try to find by searching all products and matching the generated ID
      if (!product) {
        const allProducts = await readCollection<Product>(COLLECTIONS.products);
        // Try to find by matching the generated ID pattern (name-based)
        product = allProducts.find(
          (p) => p.id === mangoId || 
          p.name.toLowerCase().replace(/\s+/g, "-") === mangoId
        ) || null;
      }
      
      return product;
    } catch (err) {
      console.error(`Error fetching product ${mangoId}:`, err);
      return null;
    }
  }, []);

  /**
   * Load cart and enrich with product details
   */
  const loadCart = useCallback(async (showLoading: boolean = true) => {
    if (!isAuthenticated || !userId) {
      setCart(null);
      setCartItemsWithProducts([]);
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const userCart = await getUserCart(userId);
      setCart(userCart);

      if (!userCart || userCart.items.length === 0) {
        setCartItemsWithProducts([]);
        return;
      }

      // Fetch product details for all items
      const itemsWithProducts = await Promise.all(
        userCart.items.map(async (item) => {
          const product = await fetchProductDetails(item.mangoId);

          if (!product) {
            // Product not found, skip this item
            return null;
          }

          // Extract price number from string (e.g., "₹899/kg" -> 899)
          const priceMatch = product.price?.match(/[\d.]+/);
          const pricePerKg = priceMatch ? parseFloat(priceMatch[0]) : 0;
          const totalPrice = pricePerKg * item.quantity;

          return {
            ...item,
            product: {
              id: product.id || item.mangoId,
              name: product.name,
              price: product.price || "₹0/kg",
              image: getSafeImageUrl(product.image),
              available: product.available,
            },
            totalPrice,
          } as CartItemWithProduct;
        })
      );

      // Filter out null items (products that weren't found)
      const validItems = itemsWithProducts.filter(
        (item): item is CartItemWithProduct => item !== null
      );

      setCartItemsWithProducts(validItems);
    } catch (err) {
      console.error("Error loading cart:", err);
      setError((err as Error).message || "Failed to load cart");
      setCartItemsWithProducts([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [isAuthenticated, userId, fetchProductDetails]);

  /**
   * Refresh cart data
   */
  const refreshCart = useCallback(async () => {
    await loadCart();
  }, [loadCart]);

  /**
   * Add item to cart
   */
  const addToCart = useCallback(
    async (mangoId: string, quantity: number = 1) => {
      if (!isAuthenticated || !userId) {
        throw new Error("User must be authenticated to add items to cart");
      }

      try {
        setError(null);
        await addToCartService(userId, mangoId, quantity);
        // Silently refresh cart in background without showing loading
        await loadCart(false);
      } catch (err) {
        console.error("Error adding to cart:", err);
        setError((err as Error).message || "Failed to add item to cart");
        throw err;
      }
    },
    [isAuthenticated, userId, loadCart]
  );

  /**
   * Update item quantity with optimistic updates
   */
  const updateQuantity = useCallback(
    async (mangoId: string, quantity: number) => {
      if (!isAuthenticated || !userId) {
        throw new Error("User must be authenticated to update cart");
      }

      if (quantity <= 0) {
        // Remove item if quantity is 0 or less - handle directly to avoid circular dependency
        setCartItemsWithProducts((prevItems) =>
          prevItems.filter((item) => item.mangoId !== mangoId)
        );
        setCart((prevCart) => {
          if (!prevCart) return prevCart;
          return {
            ...prevCart,
            items: prevCart.items.filter((item) => item.mangoId !== mangoId),
          };
        });
        try {
          setError(null);
          await removeFromCartService(userId, mangoId);
          await loadCart(false);
        } catch (err) {
          console.error("Error removing from cart:", err);
          await loadCart(false);
          setError((err as Error).message || "Failed to remove item from cart");
          throw err;
        }
        return;
      }

      // Optimistic update - update UI immediately
      setCartItemsWithProducts((prevItems) => {
        return prevItems.map((item) => {
          if (item.mangoId === mangoId) {
            // Extract price number from string (e.g., "₹899/kg" -> 899)
            const priceMatch = item.product.price?.match(/[\d.]+/);
            const pricePerKg = priceMatch ? parseFloat(priceMatch[0]) : 0;
            const totalPrice = pricePerKg * quantity;
            
            return {
              ...item,
              quantity,
              totalPrice,
            };
          }
          return item;
        });
      });

      // Update cart state optimistically
      setCart((prevCart) => {
        if (!prevCart) return prevCart;
        return {
          ...prevCart,
          items: prevCart.items.map((item) =>
            item.mangoId === mangoId ? { ...item, quantity } : item
          ),
        };
      });

      try {
        setError(null);
        await updateCartItemQuantityService(userId, mangoId, quantity);
        // Silently refresh cart in background without showing loading
        await loadCart(false);
      } catch (err) {
        console.error("Error updating cart quantity:", err);
        // Revert optimistic update on error
        await loadCart(false);
        setError((err as Error).message || "Failed to update cart quantity");
        throw err;
      }
    },
    [isAuthenticated, userId, loadCart]
  );

  /**
   * Remove item from cart with optimistic updates
   */
  const removeItem = useCallback(
    async (mangoId: string) => {
      if (!isAuthenticated || !userId) {
        throw new Error("User must be authenticated to remove items from cart");
      }

      // Optimistic update - remove from UI immediately
      setCartItemsWithProducts((prevItems) =>
        prevItems.filter((item) => item.mangoId !== mangoId)
      );

      setCart((prevCart) => {
        if (!prevCart) return prevCart;
        return {
          ...prevCart,
          items: prevCart.items.filter((item) => item.mangoId !== mangoId),
        };
      });

      try {
        setError(null);
        await removeFromCartService(userId, mangoId);
        // Silently refresh cart in background without showing loading
        await loadCart(false);
      } catch (err) {
        console.error("Error removing from cart:", err);
        // Revert optimistic update on error
        await loadCart(false);
        setError((err as Error).message || "Failed to remove item from cart");
        throw err;
      }
    },
    [isAuthenticated, userId, loadCart]
  );

  /**
   * Clear entire cart with optimistic updates
   */
  const clearCart = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      throw new Error("User must be authenticated to clear cart");
    }

    // Optimistic update - clear UI immediately
    setCartItemsWithProducts([]);
    setCart((prevCart) => {
      if (!prevCart) return prevCart;
      return {
        ...prevCart,
        items: [],
      };
    });

    try {
      setError(null);
      await clearCartService(userId);
      // Silently refresh cart in background without showing loading
      await loadCart(false);
    } catch (err) {
      console.error("Error clearing cart:", err);
      // Revert optimistic update on error
      await loadCart(false);
      setError((err as Error).message || "Failed to clear cart");
      throw err;
    }
  }, [isAuthenticated, userId, loadCart]);

  /**
   * Get total number of items in cart
   */
  const getCartItemCount = useCallback(() => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  // Load cart when user authentication changes
  useEffect(() => {
    loadCart(true);
  }, [loadCart]);

  const value = useMemo(
    () => ({
      cart,
      cartItemsWithProducts,
      loading,
      error,
      addToCart,
      updateQuantity,
      removeItem,
      clearCart,
      refreshCart,
      getCartItemCount,
    }),
    [
      cart,
      cartItemsWithProducts,
      loading,
      error,
      addToCart,
      updateQuantity,
      removeItem,
      clearCart,
      refreshCart,
      getCartItemCount,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

